import { useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Divider, IconButton, Stack, Typography, Button, MenuItem, Select } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { Dialpad, PhoneForwarded, RecordVoiceOver } from '@mui/icons-material';

import { CallsMenuStageType } from '@bandido/shared/constants';
import { CommunicationType, IVRActionType, TimeoutAction } from '@bandido/shared/types';

import { useGetAllTeams } from '@frontend/features/teams/queries';
import { WELCOME_MESSAGE } from '@frontend/features/calls-menu/constants';

import { useCallsMenu } from '../../CallsMenuContext';

import { useAudio } from '../../hooks/useAudio';
import { useStageForm } from '../../hooks/useStageForm';
import { useFileUpload } from '../../hooks/useFileUpload';
import {
  closeIconStyles,
  modalContainerStyles,
  modalContentStyles,
  modalHeaderIconStyles,
  modalHeaderStyles,
  modalPaperStyles,
} from './styles';

import { CommunicationSection } from './CommunicationSection';
import { DialpadSection } from './DialpadSection';
import { StageForm } from './StageForm';

export const SelectedStageModal = () => {
  const { t } = useTranslation();
  const { id: agencyId } = useParams();
  const { data: teams } = useGetAllTeams({
    agencyId,
    currentPage: 1,
    pageSize: 10000,
  });

  const { selectedStageId, stages, onStageChange, onCanvasClick, onDeleteStage } = useCallsMenu();
  const stage = stages.find((stage) => stage.id === selectedStageId);

  const {
    isRecording,
    recordingTime,
    audioBlob,
    isPlaying,
    currentPlayTime,
    audioFile,
    audioRef,
    fileInputRef,
    setAudioFile,
    setAudioBlob,
    startRecording,
    stopRecording,
    handlePlayAudio,
    handlePauseAudio,
    handleDeleteAudio,
    handleDeleteFile,
    formatTime,
    loadAudioFromUrl,
  } = useAudio();

  const { handleTextChange, handleSelectChange, removeAudio } = useStageForm({
    selectedStageId,
    onStageChange,
  });

  const handleCommunicationTypeChange = useCallback(
    (type: CommunicationType) => {
      if (selectedStageId) {
        if (isPlaying) {
          handlePauseAudio();
        }
        handleDeleteAudio();
        handleDeleteFile();

        onStageChange(selectedStageId, {
          communicationType: type,
        });
      }
    },
    [selectedStageId, onStageChange, handleDeleteAudio, handleDeleteFile, isPlaying, handlePauseAudio],
  );

  const { mutate, isLoading } = useFileUpload({
    selectedStageId,
    onStageChange,
    communicationType: stage?.data?.communicationType || CommunicationType.AUDIO_RECORD,
  });

  const stageIcons = {
    [CallsMenuStageType.ACTION]: <PhoneForwarded color="success" />,
    [CallsMenuStageType.SUB_MENU]: <Dialpad color="primary" />,
    main: <RecordVoiceOver color="info" />,
  };

  const element = stage?.type === CallsMenuStageType.ACTION ? 'action' : 'menu';

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (files && files.length > 0) {
      const [file] = files;
      if (file.type.startsWith('audio/')) {
        setAudioFile(file);
        setAudioBlob(null);

        if (audioRef.current) {
          URL.revokeObjectURL(audioRef.current.src);
        }

        const audioURL = URL.createObjectURL(file);
        const audio = new Audio(audioURL);
        audioRef.current = audio;

        mutate(file);
      }
    }
  };

  const handleStopRecording = useCallback(() => {
    const recordedFile = stopRecording();

    if (recordedFile) {
      if (recordedFile.size > 0) {
        mutate(recordedFile);
      }
    } else {
      setTimeout(() => {
        if (audioFile && audioBlob) {
          if (audioFile.size > 0) {
            mutate(audioFile);
          }
        }
      }, 500);
    }
  }, [stopRecording, audioFile, audioBlob, mutate]);

  const handleDeleteStep = () => {
    if (selectedStageId) {
      onDeleteStage(selectedStageId);
    }
  };

  const handleCloseModal = useCallback(() => {
    if (isPlaying && audioRef.current) {
      handlePauseAudio();
    }

    onCanvasClick();
  }, [onCanvasClick, isPlaying, audioRef, handlePauseAudio]);

  useEffect(() => {
    if (!stage) return;

    if (stage.data.communicationType === CommunicationType.AUDIO_RECORD && stage.data.audioUrl) {
      if (!audioRef.current) {
        loadAudioFromUrl(stage.data.audioUrl, CommunicationType.AUDIO_RECORD);
      }
    }

    if (stage.data.communicationType === CommunicationType.AUDIO_FILE && stage.data.fileUrl) {
      if (!audioRef.current) {
        loadAudioFromUrl(stage.data.fileUrl, CommunicationType.AUDIO_FILE);
      }
    }
  }, [stage, audioRef, loadAudioFromUrl]);

  return (
    <Box onClick={(e) => e.stopPropagation()} sx={modalContainerStyles}>
      <Paper elevation={4} sx={modalPaperStyles}>
        <Box sx={modalHeaderStyles}>
          <Stack sx={modalHeaderIconStyles}>
            {stage?.data.isMain
              ? stageIcons.main
              : stageIcons[stage?.type as keyof typeof stageIcons] ?? stageIcons.main}
            <Typography variant="h6">
              {stage?.data.isMain ? t(`callsMenu.${WELCOME_MESSAGE}`) : stage?.data.name}
            </Typography>
          </Stack>

          <IconButton
            size="small"
            aria-label="close"
            onClick={handleCloseModal}
            sx={{ color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon sx={closeIconStyles} />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box sx={modalContentStyles}>
          <StageForm
            stage={stage!}
            onTextChange={handleTextChange}
            onSelectChange={handleSelectChange}
            onCommunicationTypeChange={handleCommunicationTypeChange}
          />

          {stage?.type !== CallsMenuStageType.SUB_MENU && (
            <Stack className="nodrag" display="flex" gap={3}>
              <Stack display="flex" gap={1}>
                <Typography variant="subtitle2">{t('callsMenu.selectedStage.sendTo')}</Typography>
                <Select value={stage?.data?.action} onChange={handleSelectChange('action')} size="small" fullWidth>
                  <MenuItem value={IVRActionType.ENTER_EXTENSION}>
                    {t(`callsMenu.selectedStage.actionType.${IVRActionType.ENTER_EXTENSION}`)}
                  </MenuItem>
                  <MenuItem value={IVRActionType.SEND_TO_QUEUE}>
                    {t(`callsMenu.selectedStage.actionType.${IVRActionType.SEND_TO_QUEUE}`)}
                  </MenuItem>
                </Select>
              </Stack>
              {stage?.data?.action === IVRActionType.SEND_TO_QUEUE && (
                <Stack>
                  <Select
                    defaultValue={String(stage?.data?.teamId)}
                    value={String(stage?.data?.teamId)}
                    onChange={handleSelectChange('teamId')}
                    size="small"
                    fullWidth
                  >
                    {teams?.items?.map((team) => (
                      <MenuItem key={team.id} value={team.id}>
                        {team.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Stack>
              )}
            </Stack>
          )}
          <CommunicationSection
            sendToTeam={stage?.data?.action === IVRActionType.SEND_TO_QUEUE}
            communicationType={stage?.data?.communicationType}
            audioUrl={stage?.data?.audioUrl}
            fileUrl={stage?.data?.fileUrl}
            text={stage?.data?.text}
            isRecording={isRecording}
            recordingTime={recordingTime}
            audioFile={audioFile}
            isPlaying={isPlaying}
            currentPlayTime={currentPlayTime}
            audioRef={audioRef}
            fileInputRef={fileInputRef}
            isUploading={isLoading}
            formatTime={formatTime}
            onStartRecording={startRecording}
            onStopRecording={handleStopRecording}
            onDeleteRecording={() => {
              handleDeleteAudio();
              removeAudio('record');
            }}
            onPlayAudio={handlePlayAudio}
            onPauseAudio={handlePauseAudio}
            onTextChange={handleTextChange('text')}
            onFileUpload={handleFileUpload}
            onDeleteFile={() => {
              handleDeleteFile();
              removeAudio('file');
            }}
          />

          {!stage?.data.isMain && <DialpadSection stageDial={stage?.data?.dialOption} />}

          <Stack display="flex" flexDirection="column" gap={2}>
            <Stack gap={1}>
              <Typography variant="subtitle2">{t('callsMenu.selectedStage.timeout')}</Typography>
              <Select
                className="nodrag"
                size="small"
                value={stage?.data?.timeout?.toString() || '10'}
                onChange={handleSelectChange('timeout')}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={30}>30</MenuItem>
              </Select>
            </Stack>
          </Stack>
          <Stack display="flex" flexDirection="column" gap={2}>
            <Stack gap={1}>
              <Typography variant="subtitle2">{t('callsMenu.selectedStage.timeoutAction.label')}</Typography>
              <Select
                className="nodrag"
                size="small"
                value={stage?.data?.timeoutAction}
                onChange={handleSelectChange('timeoutAction')}
              >
                <MenuItem value={TimeoutAction.REPEAT}>
                  {t('callsMenu.selectedStage.timeoutAction.action.REPEAT')}
                </MenuItem>
                <MenuItem value={TimeoutAction.EXIT}>{t('callsMenu.selectedStage.timeoutAction.action.EXIT')}</MenuItem>
              </Select>
            </Stack>
          </Stack>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', pb: 2 }}>
          {!stage?.data.isMain && (
            <Button onClick={handleDeleteStep} startIcon={<DeleteIcon />} color="error" variant="text">
              {t(`callsMenu.selectedStage.${element}.delete`)}
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
};
